/**
 * <product-card> - central web component for product cards.
 *
 * Wraps the card markup so JS behaviors (hover state, quick-add, variant
 * swatches, analytics, etc.) can be centralized in one place rather than
 * sprinkled across templates.
 *
 * Public API:
 *   .productId   - the Shopify product id from data-product-id
 *   .productUrl  - the canonical product URL from data-product-url
 *
 * @typedef {Object} ProductCardRefs
 * @property {HTMLAnchorElement} link
 *
 * @extends {Component<ProductCardRefs>}
 */

import { Component } from "@theme/component";

class ProductCardComponent extends Component {
  requiredRefs = ["link"];

  get productId() {
    return this.dataset.productId ?? null;
  }

  get productUrl() {
    return this.dataset.productUrl ?? this.refs.link.getAttribute("href");
  }

  /**
   * Opens the global <quick-add-modal> on the mini PDP for this product.
   * Bound declaratively via `on:click="/quickAdd"` on the card's quick-add button.
   *
   * @param {Event} [event]
   */
  quickAdd(event) {
    event?.preventDefault();

    const modal = document.querySelector("quick-add-modal");
    if (modal && typeof modal.open === "function") {
      modal.open(this.productUrl);
    } else {
      // No modal on the page (or not yet defined): fall back to the full PDP.
      window.location.href = this.productUrl;
    }
  }
}

if (!customElements.get("product-card")) {
  customElements.define("product-card", ProductCardComponent);
}
